import { ViewEmployeeAbsence, ViewFoodEvent } from '../models'
import { FoodBookingModel, ServiceBookingModel } from '../models/booking'
import { CalendarEvent } from '../models/calendar'

/**
 * Extracts and parses booking data from a CalendarEvent
 * @param calendarEvent - The calendar event containing booking data
 * @returns The parsed booking model with correctly typed Date objects, or undefined if no booking data exists
 */
export const getBooking = (
  calendarEvent: CalendarEvent
): FoodBookingModel | ServiceBookingModel | undefined => {
  if (!calendarEvent.data || calendarEvent.type !== 'booking') {
    return undefined
  }

  const booking = { ...calendarEvent.data }

  // Parse from string to Date if needed
  if (typeof booking.from === 'string') {
    booking.from = new Date(booking.from)
  }

  // Parse until from string to Date if needed
  if (booking.until && typeof booking.until === 'string') {
    booking.until = new Date(booking.until)
  }

  return booking as FoodBookingModel | ServiceBookingModel
}

/**
 * Extracts the host FoodBooking carried by a closed-society background event, so the
 * management calendar can open that booking's edit sidebar on click.
 * @param calendarEvent - The calendar event containing the host booking data
 * @returns The parsed host booking, or undefined if this is not a closedSociety event
 */
export const getClosedSocietyHostBooking = (
  calendarEvent: CalendarEvent
): FoodBookingModel | undefined => {
  if (!calendarEvent.data || calendarEvent.type !== 'closedSociety') {
    return undefined
  }

  const booking = { ...calendarEvent.data } as FoodBookingModel

  if (typeof booking.from === 'string') {
    booking.from = new Date(booking.from)
  }
  if (booking.until && typeof booking.until === 'string') {
    booking.until = new Date(booking.until)
  }

  return booking
}

/**
 * Extracts absence data from a CalendarEvent
 * @param calendarEvent - The calendar event containing absence data
 * @returns The absence data, or undefined if no absence data exists
 */
export const getAbsence = (calendarEvent: CalendarEvent): ViewEmployeeAbsence | undefined => {
  if (!calendarEvent.data || calendarEvent.type !== 'absence') {
    return undefined
  }

  // Check if the data is an absence
  const absence = { ...calendarEvent.data }
  // Parse from string to Date if needed
  if (typeof absence.from === 'string') {
    absence.from = new Date(absence.from)
  }

  // Parse until from string to Date if needed
  if (absence.until && typeof absence.until === 'string') {
    absence.until = new Date(absence.until)
  }
  return absence as ViewEmployeeAbsence
}

/**
 * Extracts event data from a CalendarEvent
 * @param calendarEvent - The calendar event containing event data
 * @returns The event data, or undefined if no event data exists
 */
export const getEvent = (calendarEvent: CalendarEvent): ViewFoodEvent | undefined => {
  if (!calendarEvent.data || calendarEvent.type !== 'foodEvent') {
    return undefined
  }

  // Check if the data is an event
  const foodEvent = { ...calendarEvent.data }
  // Parse from string to Date if needed
  if (typeof foodEvent.from === 'string') {
    foodEvent.from = new Date(foodEvent.from)
  }

  // Parse until from string to Date if needed
  if (foodEvent.until && typeof foodEvent.until === 'string') {
    foodEvent.until = new Date(foodEvent.until)
  }
  return foodEvent as ViewFoodEvent
}
